import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { getBackendUrl } from '../config';
import moment from 'moment';
import './ChatPage.scss';

export default function ChatPage() {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { markAsRead, cleanMessage } = useChat();

  const [contacts, setContacts] = useState([]);
  const [bookingPartners, setBookingPartners] = useState([]);
  const [activeContactId, setActiveContactId] = useState(tutorId && tutorId !== '0' ? parseInt(tutorId) : null);
  const [activeContactInfo, setActiveContactInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  
  // Responsive State: Sollen wir auf Mobilgeräten die Liste oder den Chat zeigen?
  const [mobileView, setMobileView] = useState(tutorId && tutorId !== '0' ? 'chat' : 'list');

  const scrollRef = useRef(null);

  const getLatestTimestamp = (chat) => {
    let latest = 0;
    // Neue flache Struktur nutzen
    if (chat.nachrichten && Array.isArray(chat.nachrichten)) {
      chat.nachrichten.forEach(msg => {
        const time = new Date(msg.gesendetAm).getTime();
        if (time > latest) latest = time;
      });
    }
    return latest;
  };

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/chat`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        let data = await res.json();
        if (data && Array.isArray(data)) {
          data.sort((a, b) => getLatestTimestamp(b) - getLatestTimestamp(a));
        }
        setContacts(data || []);
        
        // Auf Desktop automatisch den ersten Kontakt wählen, falls keiner aktiv ist
        if (!activeContactId && data && data.length > 0 && window.innerWidth >= 992) {
          setActiveContactId(data[0].andererNutzer.id);
        }
      }
    } catch (err) {
      console.error("Fehler beim Laden der Kontakte:", err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchBookingPartners = async () => {
    try {
      const isTutor = user?.role === 'dozent';
      const url = isTutor ? `${getBackendUrl()}/api/anfragen/tutor` : `${getBackendUrl()}/api/anfragen`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const anfragen = await res.json();
        const partners = [];
        anfragen.forEach(a => {
          const partner = isTutor ? a.schueler : a.tutor;
          if (partner && !partners.find(p => p.id === partner.id)) {
            partners.push({
              id: partner.id,
              vollerName: partner.vollerName,
              role: isTutor ? 'student' : 'dozent'
            });
          }
        });
        setBookingPartners(partners);
      }
    } catch (err) {
      console.error("Fehler beim Laden der Buchungspartner:", err);
    }
  };

  const fetchMessages = async (contactId) => {
    if (!contactId) return;
    try {
      const res = await fetch(`${getBackendUrl()}/chat/${contactId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.andererNutzer) {
          setActiveContactInfo(data.andererNutzer);
        }

        // Neue flache Struktur: data.nachrichten
        const allMsgs = (data.nachrichten || []).map(msg => ({
          ...msg,
          nachricht: cleanMessage(msg.nachricht),
          unitInfo: msg.referenzierteLerneinheit // Falls vom Backend mitgeschickt
        }));
        
        allMsgs.sort((a, b) => new Date(a.gesendetAm) - new Date(b.gesendetAm));
        setMessages(allMsgs);
        markAsRead(contactId);
      }
    } catch (err) {
      console.error("Fehler beim Laden der Nachrichten:", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchBookingPartners();
    const interval = setInterval(fetchContacts, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (activeContactId) {
      setLoadingMessages(true);
      fetchMessages(activeContactId);

      const interval = setInterval(() => {
        fetchMessages(activeContactId);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [activeContactId, token]);

  useEffect(() => {
    if (tutorId && tutorId !== '0') {
      const newId = parseInt(tutorId);
      if (newId !== activeContactId) {
        setActiveContactId(newId);
        setMessages([]);
        setActiveContactInfo(null);
        setMobileView('chat'); // Bei Klick auf einen Kontakt in den Chat-Modus wechseln
      }
    }
  }, [tutorId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!message.trim() || !activeContactId) return;
    
    const textToSend = message;
    setMessage('');
    setError('');

    try {
      // Senden als ChatRequestDto-Objekt
      const res = await fetch(`${getBackendUrl()}/chat/${activeContactId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          nachricht: textToSend,
          lernEinheitId: null // Aktuell senden wir keine spezifische Lerneinheit mit
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Senden fehlgeschlagen." }));
        throw new Error(errorData.message || "Senden fehlgeschlagen.");
      }
      
      fetchContacts();
      fetchMessages(activeContactId);
    } catch (err) {
      setError(err.message);
      setMessage(textToSend);
    }
  };

  const handleBackToList = () => {
    setMobileView('list');
    setActiveContactId(null);
    navigate('/chat/0');
  };

  const sidebarContacts = [...contacts];
  bookingPartners.forEach(partner => {
    if (!sidebarContacts.find(c => c.andererNutzer.id === partner.id)) {
      sidebarContacts.push({
        andererNutzer: partner,
        nachrichten: []
      });
    }
  });

  if (activeContactId && activeContactInfo && !sidebarContacts.find(c => c.andererNutzer.id === activeContactId)) {
    sidebarContacts.unshift({
      andererNutzer: activeContactInfo,
      nachrichten: []
    });
  }

  return (
    <div className={`chat-layout ${mobileView === 'chat' ? 'show-chat' : 'show-list'}`}>
      {/* Sidebar - auf Mobile nur sichtbar im 'list' Modus */}
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <h5>Meine Chats</h5>
        </div>
        <div className="contact-list">
          {loadingContacts && sidebarContacts.length === 0 ? (
            <div className="text-center p-3"><Spinner size="sm" /></div>
          ) : sidebarContacts.length === 0 ? (
            <div className="text-muted p-3 text-center">Keine aktiven Chats.</div>
          ) : (
            sidebarContacts.map(chat => {
              const latestMsg = (chat.nachrichten || []).sort((a,b) => new Date(b.gesendetAm) - new Date(a.gesendetAm))[0];
              
              return (
                <div 
                  key={chat.andererNutzer.id} 
                  className={`contact-item ${chat.andererNutzer.id === activeContactId ? 'active' : ''}`}
                  onClick={() => {
                    setActiveContactId(chat.andererNutzer.id);
                    setActiveContactInfo(chat.andererNutzer);
                    setMobileView('chat');
                    navigate(`/chat/${chat.andererNutzer.id}`);
                  }}
                >
                  <div className="contact-avatar">
                    {chat.andererNutzer.vollerName?.charAt(0) || '?'}
                  </div>
                  <div className="contact-info" style={{ flexGrow: 1, minWidth: 0 }}>
                    <div className="d-flex justify-content-between align-items-baseline">
                      <div className="contact-name text-truncate">{chat.andererNutzer.vollerName}</div>
                      {latestMsg && (
                        <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: '5px' }}>
                          {moment(latestMsg.gesendetAm).format('HH:mm')}
                        </span>
                      )}
                    </div>
                    <div className="contact-role text-truncate">
                      {latestMsg ? cleanMessage(latestMsg.nachricht) : (chat.andererNutzer.role === 'dozent' ? 'Dozent' : 'Student')}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat-Bereich - auf Mobile nur sichtbar im 'chat' Modus */}
      <div className="chat-page">
        <div className="chat-header">
          <Button variant="light" className="back-button" onClick={handleBackToList}>
            &larr; <span className="d-none d-md-inline">Zurück</span>
          </Button>
          {activeContactInfo && (
            <div className="header-info">
              <div className="avatar">{activeContactInfo.vollerName?.charAt(0)}</div>
              <div className="name-status"><h5>{activeContactInfo.vollerName}</h5></div>
            </div>
          )}
        </div>

        <div className="chat-content" ref={scrollRef}>
          {!activeContactId ? (
            <div className="empty-chat-state d-none d-md-flex">
              <i className="bi bi-chat-dots"></i>
              <p>Wähle einen Chat aus, um loszulegen.</p>
            </div>
          ) : loadingMessages && messages.length === 0 ? (
             <div className="text-center mt-5"><Spinner animation="border" variant="secondary" /></div>
          ) : (
            <div className="chat-messages">
              {messages.map((msg, idx) => {
                const isMe = msg.senderId === user.id;
                const showDate = idx === 0 || moment(msg.gesendetAm).format('YYYY-MM-DD') !== moment(messages[idx-1].gesendetAm).format('YYYY-MM-DD');

                return (
                  <React.Fragment key={idx}>
                    {showDate && (
                      <div className="date-separator text-center my-3">
                        <span className="badge bg-light text-muted">
                          {moment(msg.gesendetAm).calendar(null, {
                            sameDay: '[Heute]',
                            lastDay: '[Gestern]',
                            lastWeek: 'dddd',
                            sameElse: 'DD.MM.YYYY'
                          })}
                        </span>
                      </div>
                    )}
                    <div className={`message-bubble ${isMe ? 'my-message' : 'other-message'}`}>
                      <div className="message-text">{msg.nachricht}</div>
                      <div className="message-meta">{moment(msg.gesendetAm).format('HH:mm')}</div>
                    </div>
                  </React.Fragment>
                );
              })}
              {messages.length === 0 && !loadingMessages && activeContactId && (
                <div className="text-center text-muted mt-5">
                  Starte jetzt das Gespräch mit {activeContactInfo?.vollerName || 'diesem Nutzer'}!
                </div>
              )}
            </div>
          )}
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
        </div>

        <div className="chat-footer">
          <InputGroup className="message-input-group">
            <Form.Control
              as="textarea"
              rows={1}
              placeholder="Nachricht schreiben...."
              className="message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              disabled={!activeContactId}
            />
            <Button variant="primary" className="send-button" onClick={handleSendMessage} disabled={!message.trim()}>
              <i className="bi bi-send-fill"></i>
            </Button>
          </InputGroup>
        </div>
      </div>
    </div>
  );
}
